import React, { useEffect, useMemo, useState } from 'react';
import { useUIStore, useCadastralStore } from '../../stores';
import { MapService } from '../../services/map/mapService';
import { createCadastralService } from '../../services/cadastral/cadastralService';
import './CadasterInputPanel.css';

interface CadasterInputPanelProps {
  isOpen: boolean;
  onClose: () => void;
  mapService: MapService | null;
}

const CadasterInputPanel: React.FC<CadasterInputPanelProps> = ({ isOpen, onClose, mapService }) => {
  const { addNotification } = useUIStore();
  const { 
    refCatastral, 
    setRefCatastral, 
    addToSearchHistory, 
    setLoading: setCadastralLoading,
    reset: _resetCadastralStore
  } = useCadastralStore();

  const [isVisible, setIsVisible] = useState(false);

  // Limpiar estado catastral al montar el componente (solo una vez)
  useEffect(() => {
    // Asegurar que el campo de referencia catastral esté vacío al inicializar
    if (refCatastral && refCatastral.trim() !== '') {
      setRefCatastral('');
    }
  }, []); // Solo se ejecuta una vez al montar

  // Crear instancia del servicio catastral una sola vez cuando haya mapService
  const cadastralService = useMemo(() => {
    if (!mapService) return null;
    try {
      return createCadastralService(mapService, undefined, {
        enableAutoCenter: true
      });
    } catch (e) {
      console.error('No se pudo crear CadastralService:', e);
      return null;
    }
  }, [mapService]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (cadastralService) {
        try {
          await cadastralService.init();
          if (mounted) {
            // Limpiar cualquier parcela que pudiera estar visible de sesiones anteriores
            cadastralService.clearParcel();
            const state = cadastralService.getState();
            setIsVisible(state.isVisible);
          }
        } catch (e) {
          if (mounted) console.error('Error inicializando CadastralService:', e);
        }
      }
    })();
    return () => { mounted = false; };
  }, [cadastralService]);

  if (!isOpen) return null;

  const handleShowParcel = async () => {
    if (!refCatastral.trim()) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Introduce una referencia catastral'
      });
      return;
    }
    if (!mapService || !cadastralService) {
      addNotification({ type: 'error', title: 'Mapa no listo', message: 'El mapa no está inicializado' });
      return;
    }

    try {
      setCadastralLoading(true);
      const ok = await cadastralService.showParcel(refCatastral);
      if (ok) {
        addToSearchHistory(refCatastral);
        addNotification({ type: 'success', title: 'Parcela', message: `Parcela ${refCatastral} mostrada` });
        setIsVisible(true);
      } else {
        addNotification({ type: 'warning', title: 'Parcela', message: 'No se pudo mostrar la parcela' });
      }
    } catch (e) {
      console.error('Error mostrando parcela:', e);
      addNotification({ type: 'error', title: 'Error', message: 'No se pudo cargar la parcela' });
    } finally {
      setCadastralLoading(false);
    }
  };

  const handleToggleParcel = async () => {
    if (!mapService || !cadastralService) {
      addNotification({ type: 'error', title: 'Mapa no listo', message: 'El mapa no está inicializado' });
      return;
    }
    try {
      // Si no hay parcela cargada, intenta mostrar con la REFCAT actual
      const state = cadastralService.getState();
      if (!state.currentParcel) {
        await handleShowParcel();
        return;
      }
      const nowVisible = await cadastralService.toggleParcel();
      setIsVisible(nowVisible);
      addNotification({ type: 'info', title: 'Parcela', message: nowVisible ? 'Parcela visible' : 'Parcela oculta' });
    } catch (e) {
      console.error('Error al alternar parcela:', e);
      addNotification({ type: 'error', title: 'Error', message: 'No se pudo cambiar la visibilidad' });
    }
  };

  return (
    <div className="v2-cadaster-input-panel">
      <div className="v2-cadaster-input-panel-header">
        <h3 className="v2-cadaster-input-panel-title">Geolocalización</h3>
        <button 
          className="v2-cadaster-input-panel-close"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="v2-cadaster-input-panel-content">
        <div className="v2-cadaster-input-panel-section">
          <h4>Datos Catastrales</h4>
          
          <div>
            <label className="v2-cadaster-input-panel-label">
              Referencia Catastral
            </label>
            <input 
              className="v2-cadaster-input-panel-input"
              type="text" 
              placeholder="Ej: 5874801UM4786X"
              value={refCatastral}
              onChange={(e) => setRefCatastral(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="v2-cadaster-input-panel-button"
              onClick={handleShowParcel}
            >
              Buscar parcela
            </button>
            <button 
              className="v2-cadaster-input-panel-button"
              onClick={handleToggleParcel}
              style={{ background: isVisible ? '#6c757d' : undefined }}
            >
              {isVisible ? 'Ocultar parcela' : 'Mostrar/Ocultar'}
            </button>
          </div>

          <div className="v2-cadaster-input-panel-info">
            <strong>Información:</strong>
            <ul>
              <li>Introduce la referencia catastral completa</li>
              <li>La parcela se mostrará en el mapa</li>
              <li>Se guardará en el historial de búsquedas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadasterInputPanel;
